import { CookieStorage } from 'cookie-storage';
import { atom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';

/**
 * True once the full search header has scrolled out of view, so the
 * sticky filter row knows to render a condensed search bar alongside
 * the chips. Written by `SemanticTopBar` via `useSearchScrolledOut`.
 */
export const searchScrolledOutAtom = atom(false);

export interface SearchBarTooltipV1Cookie {
	dismissed: boolean;
}

const COOKIE_NAME = 'searchBarTooltipV1';
const cookieExpiration = 1 * 365 * 24 * 60 * 60 * 1000; // 1 year
const cookieStorage = new CookieStorage();

const defaultCookieValue: SearchBarTooltipV1Cookie = {
	dismissed: false,
};

const isSearchBarTooltipV1Cookie = (
	value: unknown,
): value is SearchBarTooltipV1Cookie =>
	typeof value === 'object' &&
	value !== null &&
	typeof (value as SearchBarTooltipV1Cookie).dismissed === 'boolean';

const cookieConfig = {
	getItem: (key: string) => {
		const json = cookieStorage.getItem(key) ?? 'null';
		try {
			const value = JSON.parse(json);
			if (value === null) return defaultCookieValue;
			// Cookies are user-editable and can be corrupted or stale, so
			// valid JSON with an unexpected shape shouldn't be trusted either.
			return isSearchBarTooltipV1Cookie(value) ? value : defaultCookieValue;
		} catch {
			// Malformed/unexpected cookie content (e.g. a stale or corrupted
			// value) shouldn't crash the atom — fall back to the default.
			return defaultCookieValue;
		}
	},
	setItem: (key: string, value: SearchBarTooltipV1Cookie) => {
		const json = JSON.stringify(value);
		const expires = new Date(Date.now() + cookieExpiration);

		return cookieStorage.setItem(key, json, { expires });
	},
	removeItem: (key: string) => {
		return cookieStorage.removeItem(key);
	},
};

export const searchBarTooltipV1CookieAtom = atomWithStorage(
	COOKIE_NAME,
	null, // This null is intentional, do not change to defaultCookieValue
	cookieConfig,
);
